import { jsPDF } from "jspdf";
import { COMPANY_LOGO_URL, COMPANY_NAME } from "./companyLogo";
import {
  getRentalFileName,
  isRentalImageFile,
  isRentalPdfFile,
} from "./rentalFilePreview";

const LAYOUT = {
  margin: 18,
  footerH: 11,
  cardRadius: 2.5,
  cardGap: 8,
  innerPad: 6,
  fieldGap: 4,
};

const BRAND = {
  primary: [9, 127, 230],
  navy: [0, 9, 41],
  muted: [107, 114, 128],
  pageBg: [248, 250, 252],
  card: [255, 255, 255],
  border: [226, 232, 240],
  fieldBg: [248, 250, 252],
  footerBg: [0, 9, 41],
  white: [255, 255, 255],
  pdfTint: [239, 246, 255],
};

const MONTHS = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SECTION_META = {
  "Personal details": "Identity and contact information for verification.",
  "About me": "Applicant introduction and optional supporting documents.",
  "Address history": "Current residential address and rental reference status.",
  Employment: "Current employment details and referee information.",
  Finances: "Income sources and proof of ability to pay rent.",
  "Identity documents": "Government-issued identification on file.",
  Household: "Occupancy preferences and pet ownership.",
};

const formatDob = (dob) => {
  if (!dob?.day && !dob?.month && !dob?.year) return "—";
  const day = dob.day || "";
  const month = MONTHS[parseInt(dob.month, 10)] || dob.month || "";
  const year = dob.year || "";
  return [day, month, year].filter(Boolean).join(" ") || "—";
};

const formatStartDate = (startDate) => {
  if (!startDate?.month && !startDate?.year) return "—";
  const month = MONTHS[parseInt(startDate.month, 10)] || startDate.month || "";
  const year = startDate.year || "";
  return [month, year].filter(Boolean).join(" ") || "—";
};

const formatMoveIn = (address) => {
  if (!address?.moveInMonth && !address?.moveInYear) return null;
  const month = MONTHS[parseInt(address.moveInMonth, 10)] || address.moveInMonth;
  return [month, address.moveInYear].filter(Boolean).join(" ");
};

const formatAddress = (address) => {
  if (!address) return "—";
  const lines = [
    address.street,
    [address.city, address.state, address.postcode].filter(Boolean).join(", "),
    address.country,
    formatMoveIn(address) ? `Moved in: ${formatMoveIn(address)}` : null,
  ].filter(Boolean);
  return lines.join("\n") || "—";
};

const formatPhone = (phone) => {
  if (!phone) return "—";
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length >= 10) return digits;
  return phone;
};

const formatAmount = (amount) => {
  if (amount == null || amount === "") return "-";
  const num = Number(amount);
  if (Number.isNaN(num)) return String(amount);
  return `NGN ${num.toLocaleString("en-NG")}`;
};

const formatFrequency = (frequency) => {
  if (!frequency) return "-";
  return labelize(frequency);
};

const labelize = (value) => {
  if (!value) return "—";
  return String(value)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const loadImageFromSrc = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: img.naturalWidth,
          height: img.naturalHeight,
          format: "PNG",
        });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = reject;
    img.src = src;
  });

export const loadCompanyLogo = async () => {
  try {
    return await loadImageFromSrc(COMPANY_LOGO_URL);
  } catch {
    return null;
  }
};

const loadRemoteImage = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) return null;
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return loadImageFromSrc(dataUrl);
  } catch {
    return null;
  }
};

const scaleToFit = (w, h, maxW, maxH) => {
  const ratio = Math.min(maxW / w, maxH / h, 1);
  return { width: w * ratio, height: h * ratio };
};

class RenterProfilePdf {
  constructor() {
    this.doc = new jsPDF({ unit: "mm", format: "a4" });
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - LAYOUT.margin * 2;
    this.maxY = this.pageHeight - LAYOUT.footerH - 4;
    this.y = 0;
    this.logoImage = null;
    this.applicantName = "";
    this.generatedDate = "";
    this.totalPages = 1;
    this.inSection = false;
    this.sectionContinued = false;
  }

  setFill(rgb) {
    this.doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  }

  setDraw(rgb) {
    this.doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  }

  setText(rgb) {
    this.doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  }

  paintPageBg() {
    this.setFill(BRAND.pageBg);
    this.doc.rect(0, 0, this.pageWidth, this.pageHeight, "F");
  }

  drawLogo(x, y, maxW, maxH) {
    if (!this.logoImage) return { width: 0, height: 0 };
    const size = scaleToFit(this.logoImage.width, this.logoImage.height, maxW, maxH);
    this.setFill(BRAND.white);
    this.setDraw(BRAND.border);
    this.doc.roundedRect(x, y, size.width + 3, size.height + 3, 1.5, 1.5, "FD");
    this.doc.addImage(
      this.logoImage.dataUrl,
      this.logoImage.format,
      x + 1.5,
      y + 1.5,
      size.width,
      size.height
    );
    return size;
  }

  newPage() {
    if (this.inSection) {
      this.closeSectionFrame();
    }
    this.doc.addPage();
    this.totalPages += 1;
    this.paintPageBg();
    this.y = LAYOUT.margin;
    if (this.inSection) {
      this.sectionContinued = true;
      this.sectionTop = this.y;
      this.sectionHeaderH = 9;
      this.sectionBodyTop = this.sectionTop + this.sectionHeaderH;
      this.paintContinuationHeader();
      this.y = this.sectionBodyTop + LAYOUT.innerPad;
    }
  }

  paintContinuationHeader() {
    this.setFill(BRAND.primary);
    this.doc.roundedRect(
      LAYOUT.margin,
      this.sectionTop,
      this.contentWidth,
      this.sectionHeaderH,
      LAYOUT.cardRadius,
      LAYOUT.cardRadius,
      "F"
    );
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(9);
    this.setText(BRAND.white);
    this.doc.text(`${this.sectionTitle} (continued)`, LAYOUT.margin + 5, this.sectionTop + 6);
  }

  ensureSpace(needed) {
    if (this.y + needed > this.maxY) {
      this.newPage();
    }
  }

  drawCoverBlock(profile) {
    const pd = profile.personalDetails || {};
    this.applicantName =
      `${pd.firstName || ""} ${pd.lastName || ""}`.trim() || "Applicant";
    this.generatedDate = new Date().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    const blockH = 44;
    this.ensureSpace(blockH + 4);

    const x = LAYOUT.margin;
    const y = this.y;

    this.setFill(BRAND.navy);
    this.doc.roundedRect(x, y, this.contentWidth, blockH, 3, 3, "F");

    this.drawLogo(x + 5, y + 6, 14, 14);

    const tx = x + 24;
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(7.5);
    this.setText([147, 197, 253]);
    this.doc.text(COMPANY_NAME.toUpperCase(), tx, y + 10);

    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(17);
    this.setText(BRAND.white);
    this.doc.text("Renter Profile", tx, y + 18);

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(9);
    this.setText([203, 213, 225]);
    this.doc.text(
      "A complete summary of the applicant's profile for property managers and landlords.",
      tx,
      y + 24,
      { maxWidth: this.contentWidth - 30 }
    );

    const chipY = y + 30;
    const chipW = (this.contentWidth - 14) / 2;

    const drawChip = (cx, label, value) => {
      this.setFill([15, 23, 42]);
      this.doc.roundedRect(cx, chipY, chipW, 10, 2, 2, "F");
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(6.5);
      this.setText([148, 163, 184]);
      this.doc.text(label.toUpperCase(), cx + 3, chipY + 4);
      this.doc.setFont("helvetica", "bold");
      this.doc.setFontSize(8.5);
      this.setText(BRAND.white);
      const line = this.doc.splitTextToSize(String(value), chipW - 6)[0] || "—";
      this.doc.text(line, cx + 3, chipY + 8.5);
    };

    drawChip(x + 5, "Applicant", this.applicantName);
    drawChip(x + 8 + chipW, "Generated", this.generatedDate);

    this.y = y + blockH + LAYOUT.cardGap;
  }

  beginSection(title) {
    const desc = SECTION_META[title] || "";
    const headerBlockH = desc ? 18 : 14;
    this.ensureSpace(headerBlockH + 14);

    this.inSection = true;
    this.sectionContinued = false;
    this.sectionTitle = title;
    this.sectionDesc = desc;
    this.sectionTop = this.y;
    this.sectionHeaderH = headerBlockH;
    this.sectionBodyTop = this.y + headerBlockH;
    this.drawSectionHeader();
    this.y = this.sectionBodyTop + LAYOUT.innerPad;
  }

  drawSectionHeader() {
    this.setFill(BRAND.primary);
    this.doc.roundedRect(
      LAYOUT.margin,
      this.sectionTop,
      this.contentWidth,
      this.sectionHeaderH,
      LAYOUT.cardRadius,
      LAYOUT.cardRadius,
      "F"
    );
    this.doc.rect(
      LAYOUT.margin,
      this.sectionTop + this.sectionHeaderH - 3,
      this.contentWidth,
      3,
      "F"
    );

    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(10.5);
    this.setText(BRAND.white);
    this.doc.text(this.sectionTitle, LAYOUT.margin + 5, this.sectionTop + 7);

    if (this.sectionDesc) {
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(7.5);
      this.setText([219, 234, 254]);
      this.doc.text(this.sectionDesc, LAYOUT.margin + 5, this.sectionTop + 12.5, {
        maxWidth: this.contentWidth - 10,
      });
    }
  }

  strokeSectionCard(cardH) {
    this.setDraw(BRAND.border);
    this.doc.setLineWidth(0.25);
    this.doc.roundedRect(
      LAYOUT.margin,
      this.sectionTop,
      this.contentWidth,
      cardH,
      LAYOUT.cardRadius,
      LAYOUT.cardRadius,
      "S"
    );
  }

  closeSectionFrame() {
    const cardH = Math.max(this.sectionHeaderH + 8, this.y + LAYOUT.innerPad - this.sectionTop);
    this.strokeSectionCard(cardH);
    this.y = this.sectionTop + cardH + LAYOUT.cardGap;
  }

  endSection() {
    if (!this.inSection) return;
    const cardH = Math.max(
      this.sectionHeaderH + 8,
      this.y + LAYOUT.innerPad - this.sectionTop
    );
    this.strokeSectionCard(cardH);
    this.y = this.sectionTop + cardH + LAYOUT.cardGap;
    this.inSection = false;
    this.sectionContinued = false;
  }

  fieldHeight(label, value, width) {
    const text = value == null || value === "" ? "—" : String(value);
    const wrapped = this.doc.splitTextToSize(text, width - 8);
    return Math.max(13, wrapped.length * 4.5 + 9);
  }

  drawField(label, value, x, width) {
    const text = value == null || value === "" ? "—" : String(value);
    const wrapped = this.doc.splitTextToSize(text, width - 8);
    const h = Math.max(13, wrapped.length * 4.5 + 9);

    this.setFill(BRAND.fieldBg);
    this.setDraw(BRAND.border);
    this.doc.roundedRect(x, this.y, width, h, 2, 2, "FD");

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(6.5);
    this.setText(BRAND.muted);
    this.doc.text(label.toUpperCase(), x + 4, this.y + 4.5);

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(9.5);
    this.setText(BRAND.navy);
    this.doc.text(wrapped, x + 4, this.y + 9.5);

    return h;
  }

  drawSubsectionTitle(title, skipEnsure = false) {
    if (!skipEnsure) {
      this.ensureSpace(9);
    }
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(9);
    this.setText(BRAND.navy);
    this.doc.text(title, LAYOUT.margin + LAYOUT.innerPad, this.y + 4);
    this.y += 9;
  }

  drawIncomeSource(index, src) {
    const fields = [
      ["Income type", src.incomeType || "-"],
      ["Amount", formatAmount(src.amount)],
      ["Frequency", formatFrequency(src.frequency)],
    ];
    const rowH = this.fieldRowHeight(fields, 3);
    this.ensureSpace(9 + rowH + LAYOUT.fieldGap);
    this.drawSubsectionTitle(`Income source ${index + 1}`, true);
    this.drawFieldRow(fields, true);
  }

  fieldRowHeight(fields, columns = fields.length) {
    const pad = LAYOUT.innerPad;
    const gap = LAYOUT.fieldGap;
    const totalGap = gap * (columns - 1);
    const colW = (this.contentWidth - pad * 2 - totalGap) / columns;
    return Math.max(...fields.map(([label, value]) => this.fieldHeight(label, value, colW)));
  }

  drawFieldRow(fields, skipEnsure = false) {
    const pad = LAYOUT.innerPad;
    const gap = LAYOUT.fieldGap;
    const count = fields.length;
    const totalGap = gap * (count - 1);
    const colW = (this.contentWidth - pad * 2 - totalGap) / count;
    const startX = LAYOUT.margin + pad;

    const rowH = Math.max(
      ...fields.map(([label, value]) => this.fieldHeight(label, value, colW))
    );

    if (!skipEnsure) {
      this.ensureSpace(rowH + gap);
    }
    const rowY = this.y;

    fields.forEach(([label, value], i) => {
      this.y = rowY;
      this.drawField(label, value, startX + i * (colW + gap), colW);
    });

    this.y = rowY + rowH + gap;
  }

  drawFieldGrid(rows) {
    const pad = LAYOUT.innerPad;
    const gap = LAYOUT.fieldGap;
    const colW = (this.contentWidth - pad * 2 - gap) / 2;
    const leftX = LAYOUT.margin + pad;
    const rightX = leftX + colW + gap;
    let rowY = this.y;

    for (let i = 0; i < rows.length; i += 2) {
      const left = rows[i];
      const right = rows[i + 1];
      const leftH = this.fieldHeight(left[0], left[1], colW);
      const rightH = right ? this.fieldHeight(right[0], right[1], colW) : 0;
      const rowH = Math.max(leftH, rightH);

      this.ensureSpace(rowH + gap);
      rowY = this.y;

      this.y = rowY;
      this.drawField(left[0], left[1], leftX, colW);

      if (right) {
        this.y = rowY;
        this.drawField(right[0], right[1], rightX, colW);
      }

      this.y = rowY + rowH + gap;
    }
  }

  drawFullField(label, value) {
    const text = value == null || value === "" ? "—" : String(value);
    const wrapped = this.doc.splitTextToSize(text, this.contentWidth - LAYOUT.innerPad * 2 - 8);
    const h = wrapped.length * 4.5 + 11;
    this.ensureSpace(h + LAYOUT.fieldGap);

    const x = LAYOUT.margin + LAYOUT.innerPad;
    const w = this.contentWidth - LAYOUT.innerPad * 2;

    this.setFill(BRAND.fieldBg);
    this.setDraw(BRAND.border);
    this.doc.roundedRect(x, this.y, w, h, 2, 2, "FD");

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(6.5);
    this.setText(BRAND.muted);
    this.doc.text(label.toUpperCase(), x + 4, this.y + 5);

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(9.5);
    this.setText(BRAND.navy);
    this.doc.text(wrapped, x + 4, this.y + 10.5);

    this.y += h + LAYOUT.fieldGap;
  }

  async drawAttachment(label, fileMeta) {
    const x = LAYOUT.margin + LAYOUT.innerPad;
    const w = this.contentWidth - LAYOUT.innerPad * 2;

    if (!fileMeta?.url && !fileMeta?.fileName) {
      this.drawFullField(label, "Not provided");
      return;
    }

    const fileName = getRentalFileName(null, fileMeta);
    const isImage = isRentalImageFile(null, fileMeta);
    const isPdf = isRentalPdfFile(null, fileMeta);

    if (isImage && fileMeta.url) {
      const image = await loadRemoteImage(fileMeta.url);
      if (image) {
        const imgSize = scaleToFit(image.width, image.height, w - 8, 50);
        const h = imgSize.height + 22;
        this.ensureSpace(h + LAYOUT.fieldGap);

        this.setFill(BRAND.fieldBg);
        this.setDraw(BRAND.border);
        this.doc.roundedRect(x, this.y, w, h, 2, 2, "FD");

        this.doc.setFont("helvetica", "normal");
        this.doc.setFontSize(6.5);
        this.setText(BRAND.muted);
        this.doc.text(label.toUpperCase(), x + 4, this.y + 5);

        this.setFill(BRAND.white);
        this.setDraw(BRAND.border);
        this.doc.roundedRect(x + 4, this.y + 8, imgSize.width + 4, imgSize.height + 4, 1.5, 1.5, "FD");
        this.doc.addImage(
          image.dataUrl,
          image.format,
          x + 6,
          this.y + 10,
          imgSize.width,
          imgSize.height
        );

        this.doc.setFont("helvetica", "normal");
        this.doc.setFontSize(7.5);
        this.setText(BRAND.muted);
        this.doc.text(`File: ${fileName}`, x + 4, this.y + h - 4);

        this.y += h + LAYOUT.fieldGap;
        return;
      }
    }

    const h = 16;
    this.ensureSpace(h + LAYOUT.fieldGap);

    this.setFill(isPdf ? BRAND.pdfTint : BRAND.fieldBg);
    this.setDraw(BRAND.border);
    this.doc.roundedRect(x, this.y, w, h, 2, 2, "FD");

    this.setFill(BRAND.primary);
    this.doc.roundedRect(x + 4, this.y + 3, 10, 10, 1.5, 1.5, "F");
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(6.5);
    this.setText(BRAND.white);
    this.doc.text(isPdf ? "PDF" : "FILE", x + 5.8, this.y + 9.5);

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(6.5);
    this.setText(BRAND.muted);
    this.doc.text(label.toUpperCase(), x + 17, this.y + 6);

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(9.5);
    this.setText(BRAND.navy);
    this.doc.text(fileName, x + 17, this.y + 10.5);

    this.doc.setFont("helvetica", "italic");
    this.doc.setFontSize(7);
    this.setText(BRAND.muted);
    this.doc.text(
      isPdf ? "PDF document attached to profile" : "Document attached to profile",
      x + 17,
      this.y + 14
    );

    this.y += h + LAYOUT.fieldGap;
  }

  drawAllFooters() {
    const total = this.doc.getNumberOfPages();
    const textY = this.pageHeight - LAYOUT.footerH / 2 + 1;

    for (let page = 1; page <= total; page += 1) {
      this.doc.setPage(page);
      const fy = this.pageHeight - LAYOUT.footerH;

      this.setFill(BRAND.footerBg);
      this.doc.rect(0, fy, this.pageWidth, LAYOUT.footerH, "F");

      this.setDraw([30, 41, 59]);
      this.doc.line(0, fy, this.pageWidth, fy);

      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(8);
      this.setText([203, 213, 225]);
      this.doc.text(COMPANY_NAME, LAYOUT.margin, textY);

      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(8);
      this.setText(BRAND.white);
      this.doc.text(`Page ${page} of ${total}`, this.pageWidth - LAYOUT.margin, textY, {
        align: "right",
      });
    }
  }

  async build(profile) {
    const pd = profile.personalDetails || {};
    const about = profile.aboutMe || {};
    const address = profile.addressHistory?.currentAddress;
    const emp = profile.employment || {};
    const ref = emp.reference || {};
    const finances = profile.finances || {};
    const ids = profile.identityDocuments || {};
    const people = profile.householdPeople || {};
    const pets = profile.householdPets || {};

    this.logoImage = await loadCompanyLogo();
    this.paintPageBg();
    this.y = LAYOUT.margin;
    this.drawCoverBlock(profile);

    this.beginSection("Personal details");
    this.drawFieldGrid([
      ["Full name", this.applicantName],
      ["Date of birth", formatDob(pd.dateOfBirth)],
      ["Phone number", formatPhone(pd.phone)],
      ["Email address", pd.email],
    ]);
    this.endSection();

    this.beginSection("About me");
    this.drawFullField("Introduction", about.aboutText);
    await this.drawAttachment("Supporting document", about.supportingDocument);
    this.endSection();

    this.beginSection("Address history");
    this.drawFullField("Current address", formatAddress(address));
    this.drawFieldGrid([
      [
        "Landlord reference",
        address?.addReference === "yes" ? "Reference will be provided" : "Not provided",
      ],
    ]);
    this.endSection();

    this.beginSection("Employment");
    this.drawFieldGrid([
      ["Employment type", labelize(emp.employmentType)],
      ["Company", emp.companyName],
      ["Role / title", emp.roleTitle],
      ["Start date", formatStartDate(emp.startDate)],
      ["Reference type", labelize(ref.type)],
      ["Reference name", ref.fullName],
      ["Reference email", ref.email],
      ["Reference phone", formatPhone(ref.phone)],
    ]);
    this.endSection();

    this.beginSection("Finances");
    if (finances.noIncome) {
      this.drawFullField("Income status", "Applicant declared no current income.");
    } else if (finances.incomeSources?.length) {
      finances.incomeSources.forEach((src, i) => {
        this.drawIncomeSource(i, src);
      });
    } else {
      this.drawFullField("Income sources", "Not provided");
    }
    if (finances.proofDocuments?.length) {
      for (let i = 0; i < finances.proofDocuments.length; i += 1) {
        await this.drawAttachment(`Proof of income ${i + 1}`, finances.proofDocuments[i]);
      }
    } else {
      this.drawFullField("Proof of income", "Not provided");
    }
    this.endSection();

    this.beginSection("Identity documents");
    await this.drawAttachment("Photo identification", ids.photoId);
    await this.drawAttachment("Secondary identification", ids.secondId);
    this.endSection();

    this.beginSection("Household");
    this.drawFieldGrid([
      [
        "Other occupants",
        people.liveWithOthers === "yes"
          ? "Will live with others not on the lease"
          : "Will not live with others not on the lease",
      ],
      [
        "Pets",
        `Dogs: ${pets.dogs ?? 0}   Cats: ${pets.cats ?? 0}   Other: ${pets.other ?? 0}`,
      ],
    ]);
    this.endSection();

    this.drawAllFooters();

    const fileName = `renter-profile-${pd.lastName || "profile"}.pdf`.replace(/\s+/g, "-");
    this.doc.save(fileName.toLowerCase());
  }
}

export const downloadRenterProfilePdf = async (profile) => {
  const builder = new RenterProfilePdf();
  await builder.build(profile);
};
